import { Badge } from '@/components/ui/badge';
import { EntityLink } from '@/components/ui/entity-link';
import { useEntityModalContext } from '@/contexts/EntityModalContext';
import { getVehicleMakeLogoUrl } from '@/hooks/useVehicleMakesModels';
import { cn } from '@/lib/utils';
import { Bike, Bus, CarFront, Truck } from 'lucide-react';
import { useEffect, useState } from 'react';

interface VehicleDisplayItemProps {
	vehicleId?: string | null;
	plateNumber?: string | null;
	vehicleTypeName?: string | null;
	make?: string | null;
	model?: string | null;
	color?: string | null;
	isActive?: boolean | null;
	inactiveLabel?: string;
	tags?: string[];
	className?: string;
}

interface VehicleDisplayListProps {
	children: React.ReactNode;
	className?: string;
}

/**
 * Wrapper component that creates a CSS grid context for VehicleDisplayItem rows.
 * Uses subgrid so all vehicle items share consistent column widths (table-like alignment).
 * Columns: [icon] [plate] [details] [tags]
 */
export function VehicleDisplayList({ children, className }: VehicleDisplayListProps) {
	return (
		<div
			className={cn('grid items-center text-sm', className)}
			style={{ gridTemplateColumns: '2rem auto 1fr auto' }}
		>
			{children}
		</div>
	);
}

const resolveVehicleIcon = (vehicleTypeName?: string | null) => {
	const normalizedType = vehicleTypeName?.toLowerCase() ?? '';

	if (normalizedType.includes('motor')) {
		return Bike;
	}

	if (normalizedType.includes('truck') || normalizedType.includes('lorry')) {
		return Truck;
	}

	if (normalizedType.includes('bus')) {
		return Bus;
	}

	return CarFront;
};

const compact = (value?: string | null) => {
	const normalized = value?.trim();
	return normalized ? normalized : null;
};

export function VehicleDisplayItem({
	vehicleId,
	plateNumber,
	vehicleTypeName,
	make,
	model,
	color,
	isActive,
	inactiveLabel = 'Inactive',
	tags = [],
	className
}: VehicleDisplayItemProps) {
	const { openEntityModal } = useEntityModalContext();
	const VehicleIcon = resolveVehicleIcon(vehicleTypeName);
	const logoUrl = getVehicleMakeLogoUrl(make);
	const [logoError, setLogoError] = useState(false);
	const makeValue = compact(make);
	const modelValue = compact(model);
	const typeValue = compact(vehicleTypeName);
	const colorValue = compact(color);
	const displayPlate = compact(plateNumber) ?? compact(vehicleId) ?? 'Unknown plate';

	const makeModelValue =
		makeValue && modelValue
			? `${makeValue} ${modelValue}`
			: makeValue ?? modelValue;

	const metaValues = [typeValue, makeModelValue, colorValue].filter(
		(value): value is string => Boolean(value)
	);
	const detailsText = metaValues.join(' - ');
	const shouldShowInactiveBadge =
		typeof isActive === 'boolean' && isActive === false;
	const hasLogo = Boolean(logoUrl && !logoError);
	const uniqueTags = Array.from(
		new Set(
			tags
				.map(tag => tag.trim())
				.filter(tag => Boolean(tag))
		)
	);

	useEffect(() => {
		setLogoError(false);
	}, [logoUrl]);

	return (
		<div
			className={cn(
				'col-span-full grid items-center gap-x-3',
				className
			)}
			style={{ gridTemplateColumns: 'subgrid' }}
		>
			{/* Column 1: Icon */}
			<div
				className={cn(
					'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-transparent overflow-hidden',
					!hasLogo && 'border border-gray-200 dark:border-gray-600'
				)}
			>
				{hasLogo ? (
					<img
						src={logoUrl ?? undefined}
						alt={makeValue ? `${makeValue} logo` : 'Vehicle logo'}
						className="h-full w-full object-cover"
						onError={() => setLogoError(true)}
					/>
				) : (
					<VehicleIcon className="h-4 w-4 text-gray-400 dark:text-gray-400" />
				)}
			</div>

			{/* Column 2: Plate number */}
			<div className="flex items-center gap-2 min-w-0">
				{vehicleId ? (
					<EntityLink
						entityId={vehicleId}
						entityType="vehicles"
						displayText={displayPlate}
						onEntityClick={openEntityModal}
						className="text-sm font-medium text-primary whitespace-nowrap"
					/>
				) : (
					<span className="truncate text-sm font-medium text-foreground whitespace-nowrap">
						{displayPlate}
					</span>
				)}
			</div>

			{/* Column 3: Details (type - make model - color) */}
			<div className="min-w-0">
				{detailsText && (
					<span className="text-sm text-muted-foreground whitespace-nowrap">
						{detailsText}
					</span>
				)}
			</div>

			{/* Column 4: Tags & Status */}
			<div className="flex flex-wrap items-center gap-1.5">
				{uniqueTags.map(tag => (
					<Badge key={tag} variant="outline">
						{tag}
					</Badge>
				))}
				{shouldShowInactiveBadge && (
					<Badge
						variant="outline"
						className="border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200"
					>
						{inactiveLabel}
					</Badge>
				)}
			</div>
		</div>
	);
}
